import { useCrossSellTracker } from "@packages/polling-checker"
import { Button, ButtonGroup, Divider, Text } from "@shopify/polaris"
import classNames from "classnames"
import { Fragment, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import settingsApi from "@/api/settings"
import { CROSS_SELL_POINT_ENUM } from "@/enums/CrossSellPoint"

import styles from "./index.module.scss"

interface RecommendedModuleProps {
  className?: string
}

interface Feature {
  id: number
  title: string
  description: string
  buttonText: string
  img: string | null
  url?: string | null
  type: number
}

interface Category {
  name: string
  features: Feature[]
}

// 定义功能类型对应的路由配置
const FEATURE_ROUTES = {
  // Less WISMO
  1: { path: "/settings/general", state: { shopifyFeaturesTip: true } },
  2: { path: "/settings/shipping-notifications", state: { /* notificationsTip: true*/ } },
  3: { path: "/tracking-page/estimatedDeliveryTime", state: { /* prePurchaseEddTip: true */ } },
  4: { path: "/tracking-page/customShipmentStatus", state: {  /* customStatusTip: true */ } },
  5: { path: "/tracking-page/languages", state: { autoTranslateTip: true } },

  // More Revenue
  6: { path: "/tracking-page/productRecommendation", state: { productRecommendationsTip: false } },
  7: { path: "/settings/shipping-notifications", state: { discountTip: true } },
  8: { path: "/settings/pre_purchase_edd", state: { /* prePurchaseEDDTip: true */ } },

  // Easier Integrations
  9: { path: "/integration", state: { integrationsTip: true } },

  // Better Post-purchase
  11: { path: "/protection", state: { protectionTip: true } },
} as const

const RecommendedModule = ({
  className,
}: RecommendedModuleProps) => {
  const { t } = useTranslation("dashboard")
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(0)
  const [buttonIDLoading, setButtonIDLoading] = useState(0)

  // 使用 useCallback 优化性能
  const handleFeatureClick = useCallback((feature: Feature) => {
    if (feature.id === 10){
      window.CrossSellTracker?.redirectToReturnInstall(CROSS_SELL_POINT_ENUM.RecommendedReturn, {
        from: "parcelpanel",
      })
    }
    if (feature.id === 11){
      window.CrossSellTracker?.logClicked(CROSS_SELL_POINT_ENUM.RecommendedProtection)
      // 跳转PP insurance前，将traceId存储到sessionStorage，避免刷新后丢失
      sessionStorage.setItem("crossSellToInsuranceTraceId", window.CrossSellTracker?.getTraceId() || "")
    }
    if (feature.id === 7){
      setButtonIDLoading(7)
      settingsApi.getShippingNotificationsConfig().then(({ data: { shipping_notifications } }) => {
        const statusList = ["info_received", "in_transit", "pickup", "ready_for_pickup", "delivered", "exception", "failed_attempt"]
        let targetTemplate = "info_received"

        for (const item of statusList) {
          if (shipping_notifications.status_enabled && shipping_notifications.status_enabled[item] ) {
            targetTemplate = item
            break
          }
        }

        const target = `/settings/email-edit/${targetTemplate}`

        if (target) {
          navigate(target, { state: { discountTip: true } })
        }
        return
      })
        .finally(() => {
          setButtonIDLoading(0)
        })
    } else {
      const route = FEATURE_ROUTES[feature.type as keyof typeof FEATURE_ROUTES]

      if (route && route.path) {
        navigate(route.path, { state: route.state || {} })
      } else if (feature.url) {
        window.open(feature.url, "_blank", "noopener,noreferrer")
      }
    }
  }, [navigate])

  // 使用 useMemo 缓存 categories 数据
  const categories: Category[] = useMemo(() => [
    {
      "name": t("Recommended.categories.lessWismo.name"),
      "features": [
        {
          "id": 1,
          "title": t("Recommended.categories.lessWismo.features.shopifyFeatures.title"),
          "description": t("Recommended.categories.lessWismo.features.shopifyFeatures.description"),
          "buttonText": t("Recommended.categories.lessWismo.features.shopifyFeatures.buttonText"),
          "img": "https://cdn.parcelpanel.com/front-end/2025/dashboard/recommended_1.svg",
          "url": null,
          "type": 1,
        },
        {
          "id": 2,
          "title": t("Recommended.categories.lessWismo.features.shippingNotifications.title"),
          "description": t("Recommended.categories.lessWismo.features.shippingNotifications.description"),
          "buttonText": t("Recommended.categories.lessWismo.features.shippingNotifications.buttonText"),
          "img": "https://cdn.parcelpanel.com/front-end/2025/dashboard/recommended_2.png",
          "url": null,
          "type": 2,
        },
        {
          "id": 3,
          "title": t("Recommended.categories.lessWismo.features.ProvideEDD.title"),
          "description": t("Recommended.categories.lessWismo.features.ProvideEDD.description"),
          "buttonText": t("Recommended.categories.lessWismo.features.ProvideEDD.buttonText"),
          "img": "https://cdn.parcelpanel.com/front-end/2025/dashboard/recommended_3.svg",
          "url": null,
          "type": 3,
        },
        {
          "id": 4,
          "title": t("Recommended.categories.lessWismo.features.AddShipmentStatus.title"),
          "description": t("Recommended.categories.lessWismo.features.AddShipmentStatus.description"),
          "buttonText": t("Recommended.categories.lessWismo.features.AddShipmentStatus.buttonText"),
          "img": "https://cdn.parcelpanel.com/front-end/2025/dashboard/recommended_4.svg",
          "url": null,
          "type": 4,
        },
        {
          "id": 5,
          "title": t("Recommended.categories.lessWismo.features.AutoTranslateTracking.title"),
          "description": t("Recommended.categories.lessWismo.features.AutoTranslateTracking.description"),
          "buttonText": t("Recommended.categories.lessWismo.features.AutoTranslateTracking.buttonText"),
          "img": "https://cdn.parcelpanel.com/front-end/2025/dashboard/recommended_5.png",
          "url": null,
          "type": 5,
        },
      ],
    },
    {
      "name": t("Recommended.categories.moreRevenue.name"),
      "features": [
        {
          "id": 6,
          "title": t("Recommended.categories.moreRevenue.features.ShowcaseProductRecommendations.title"),
          "description": t("Recommended.categories.moreRevenue.features.ShowcaseProductRecommendations.description"),
          "buttonText": t("Recommended.categories.moreRevenue.features.ShowcaseProductRecommendations.buttonText"),
          "img": "https://cdn.parcelpanel.com/front-end/2025/dashboard/recommended_6.svg",
          "url": null,
          "type": 6,
        },
        {
          "id": 7,
          "title": t("Recommended.categories.moreRevenue.features.IncludeDiscountCodes.title"),
          "description": t("Recommended.categories.moreRevenue.features.IncludeDiscountCodes.description"),
          "buttonText": t("Recommended.categories.moreRevenue.features.IncludeDiscountCodes.buttonText"),
          "img": "https://cdn.parcelpanel.com/front-end/2025/dashboard/recommended_7.png",
          "url": null,
          "type": 7,
        },
        {
          "id": 8,
          "title": t("Recommended.categories.moreRevenue.features.DisplayEDD.title"),
          "description": t("Recommended.categories.moreRevenue.features.DisplayEDD.description"),
          "buttonText": t("Recommended.categories.moreRevenue.features.DisplayEDD.buttonText"),
          "img": "https://cdn.parcelpanel.com/front-end/2025/dashboard/recommended_8.png",
          "url": null,
          "type": 8,
        },
      ],
    },
    {
      "name": t("Recommended.categories.easierIntegrations.name"),
      "features": [
        {
          "id": 9,
          "title": t("Recommended.categories.easierIntegrations.features.IntegrateAPP.title"),
          "description": t("Recommended.categories.easierIntegrations.features.IntegrateAPP.description"),
          "buttonText": t("Recommended.categories.easierIntegrations.features.IntegrateAPP.buttonText"),
          "img": "https://cdn.parcelpanel.com/front-end/2025/dashboard/recommended_9.svg",
          "url": null,
          "type": 9,
        },
      ],
    },
    {
      "name": t("Recommended.categories.betterPostPurchase.name"),
      "features": [
        {
          "id": 10,
          "title": "CWILL Returns & Exchanges",
          "description": t("Recommended.categories.betterPostPurchase.features.ParcelWILLReturnsExchanges.description"),
          "buttonText": t("ParcelWILLReturnsExchangesButtonText"),
          "img": "https://cdn.parcelpanel.com/front-end/2025/dashboard/recommended_10.png",
          // "url": "https://apps.shopify.com/parcel-panel-returns-exchanges?from=parcelpanel",
          "type": 10,
        },
        {
          "id": 11,
          "title": "CWILL Shipping Protection",
          "description": t("Recommended.categories.betterPostPurchase.features.ParcelWILLShippingProtection.description"),
          "buttonText": t("ParcelWILLShippingProtectionButtonText1"),
          "img": "https://cdn.parcelpanel.com/front-end/2025/dashboard/recommended_11.png",
          "url": null,
          "type": 11,
        },
      ],
    },
  ], [t]) // 依赖于 t 函数

  // 使用简化的 CrossSellTracker 检查器
  useCrossSellTracker(
    () => {
      window.CrossSellTracker?.logViewed(CROSS_SELL_POINT_ENUM.RecommendedReturn)
      window.CrossSellTracker?.logViewed(CROSS_SELL_POINT_ENUM.RecommendedProtection)
    },
    activeTab === 3,
  )
  return (
    <div className={classNames(styles.container, className)}>
      <ButtonGroup variant="segmented" fullWidth>
        {categories.map((item, index) => (
          <Button
            size={"large"}
            key={item.name}
            pressed={activeTab === index}
            onClick={() => setActiveTab(index)}
          >
            {item.name}
          </Button>
        ))}
      </ButtonGroup>

      <div className={styles.content}>
        {categories[activeTab].features.map((feature, index) => (
          <Fragment key={feature.id}>
            <div className={styles.featureItem}>
              <div className={styles.featureContent}>
                <div className={styles.textContent}>
                  <Text variant="headingSm" as="h3">
                    {feature.title}
                  </Text>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {feature.description}
                  </Text>
                  <div className={"mt-2"}>
                    <Button loading={buttonIDLoading === feature.id} onClick={() => handleFeatureClick(feature)}>
                      {feature.buttonText}
                    </Button>
                  </div>
                </div>

                <div className={styles.imageWrapper} style={{ width: 200 }}>
                  {feature.img && <img src={feature.img} loading="lazy" />}
                </div>
              </div>
            </div>
            {index !== categories[activeTab].features.length - 1 && <Divider />}
          </Fragment>
        ))}
      </div>
    </div>
  )
}

export default RecommendedModule
